import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, Plus, Search, MoreHorizontal, CheckCircle, 
  Clock, AlertCircle, XCircle, Loader2, Trash2, Edit
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const EmpresasPage = () => {
  const { api } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para edição
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [empresaToEdit, setEmpresaToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cnae: '',
    endereco: '',
    telefone: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cnae: '',
    endereco: '',
    telefone: '',
    email: '',
    plano_id: ''
  });

  useEffect(() => {
    fetchEmpresas();
    fetchPlanos();
  }, []);

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error fetching empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanos = async () => {
    try {
      const response = await api.get('/planos');
      setPlanos(response.data);
    } catch (error) {
      console.error('Error fetching planos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/empresas', formData);
      toast.success('Empresa cadastrada com sucesso!');
      setDialogOpen(false);
      setFormData({
        razao_social: '',
        nome_fantasia: '',
        cnpj: '',
        cnae: '',
        endereco: '',
        telefone: '',
        email: '',
        plano_id: ''
      });
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar empresa');
    }
  };

  const updateStatus = async (empresaId, status) => {
    try {
      await api.put(`/empresas/${empresaId}/status?status=${status}`);
      toast.success('Status atualizado!');
      fetchEmpresas();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteClick = (empresa) => {
    setEmpresaToDelete(empresa);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!empresaToDelete) return;
    
    setDeleting(true);
    try {
      const response = await api.delete(`/empresas/${empresaToDelete.id}?excluir_dados=true`);
      toast.success(response.data.message || 'Empresa excluída com sucesso!');
      
      if (response.data.dados_excluidos) {
        const dados = response.data.dados_excluidos;
        const items = [];
        if (dados.empregados > 0) items.push(`${dados.empregados} empregado(s)`);
        if (dados.usuarios > 0) items.push(`${dados.usuarios} usuário(s)`);
        if (dados.marcacoes > 0) items.push(`${dados.marcacoes} marcação(ões)`);
        if (dados.jornadas > 0) items.push(`${dados.jornadas} jornada(s)`);
        
        if (items.length > 0) {
          toast.info(`Dados removidos: ${items.join(', ')}`);
        }
      }
      
      setDeleteDialogOpen(false);
      setEmpresaToDelete(null);
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir empresa');
    } finally {
      setDeleting(false);
    }
  };

  // Funções de edição
  const handleEditClick = (empresa) => {
    setEmpresaToEdit(empresa);
    setEditFormData({
      razao_social: empresa.razao_social || '',
      nome_fantasia: empresa.nome_fantasia || '',
      cnpj: empresa.cnpj || '',
      cnae: empresa.cnae || '',
      endereco: empresa.endereco || '',
      telefone: empresa.telefone || '',
      email: empresa.email || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    setSaving(true);
    try {
      await api.put(`/empresas/${empresaToEdit.id}`, editFormData);
      toast.success('Empresa atualizada com sucesso!');
      setEditDialogOpen(false);
      setEmpresaToEdit(null);
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar empresa');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { label: 'Pendente', variant: 'secondary', icon: Clock },
      teste: { label: 'Teste', variant: 'default', icon: AlertCircle },
      ativa: { label: 'Ativa', variant: 'default', icon: CheckCircle },
      suspensa: { label: 'Suspensa', variant: 'destructive', icon: XCircle },
      cancelada: { label: 'Cancelada', variant: 'destructive', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pendente;
    return (
      <Badge variant={config.variant} className={status === 'ativa' ? 'bg-accent' : ''}>
        <config.icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredEmpresas = empresas.filter(e => 
    e.razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.cnpj?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="empresas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Empresas</h2>
          <p className="text-muted-foreground">Gerencie as empresas da plataforma</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="nova-empresa-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Cadastrar Empresa</DialogTitle>
              <DialogDescription>Preencha os dados da nova empresa cliente</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={formData.razao_social}
                    onChange={(e) => setFormData({...formData, razao_social: e.target.value})}
                    required
                    data-testid="input-razao-social"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nome_fantasia">Nome Fantasia *</Label>
                  <Input
                    id="nome_fantasia"
                    value={formData.nome_fantasia}
                    onChange={(e) => setFormData({...formData, nome_fantasia: e.target.value})}
                    required
                    data-testid="input-nome-fantasia"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                    placeholder="00.000.000/0000-00"
                    required
                    data-testid="input-cnpj"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnae">CNAE *</Label>
                  <Input
                    id="cnae"
                    value={formData.cnae}
                    onChange={(e) => setFormData({...formData, cnae: e.target.value})}
                    placeholder="0000-0/00"
                    required
                    data-testid="input-cnae"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                    data-testid="input-telefone"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                    data-testid="input-endereco"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="plano">Plano</Label>
                  <Select
                    value={formData.plano_id}
                    onValueChange={(value) => setFormData({...formData, plano_id: value})}
                  >
                    <SelectTrigger data-testid="select-plano">
                      <SelectValue placeholder="Selecione um plano" />
                    </SelectTrigger>
                    <SelectContent>
                      {planos.map(plano => (
                        <SelectItem key={plano.id} value={plano.id}>
                          {plano.nome} - R$ {plano.valor_mensal}/mês
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-empresa-btn">
                  Cadastrar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-empresa"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEmpresas.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhuma empresa encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>CNAE</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Cadastro</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmpresas.map((empresa) => (
                    <TableRow key={empresa.id} data-testid={`empresa-row-${empresa.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{empresa.razao_social}</p>
                          <p className="text-sm text-muted-foreground">{empresa.nome_fantasia}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{empresa.cnpj}</TableCell>
                      <TableCell className="font-mono text-sm">{empresa.cnae}</TableCell>
                      <TableCell>{getStatusBadge(empresa.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(empresa.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`empresa-actions-${empresa.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleEditClick(empresa)}
                              data-testid={`editar-empresa-${empresa.id}`}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Editar Cadastro
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => updateStatus(empresa.id, 'ativa')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Ativar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(empresa.id, 'teste')}>
                              <Clock className="w-4 h-4 mr-2" />
                              Período de Teste
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(empresa.id, 'suspensa')} className="text-destructive">
                              <XCircle className="w-4 h-4 mr-2" />
                              Suspender
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(empresa)} 
                              className="text-destructive focus:text-destructive"
                              data-testid={`excluir-empresa-${empresa.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir Empresa
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert Dialog para confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Excluir Empresa
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Você está prestes a excluir permanentemente a empresa:
              </p>
              {empresaToDelete && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="font-semibold text-slate-900">{empresaToDelete.razao_social}</p>
                  <p className="text-sm text-slate-500">{empresaToDelete.nome_fantasia}</p>
                  <p className="text-sm font-mono text-slate-400">{empresaToDelete.cnpj}</p>
                </div>
              )}
              <p className="text-destructive font-medium">
                Esta ação irá excluir TODOS os dados relacionados:
              </p>
              <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                <li>Empregados cadastrados</li>
                <li>Usuários (admins da empresa)</li>
                <li>Marcações de ponto</li>
                <li>Jornadas processadas</li>
                <li>Ocorrências e justificativas</li>
                <li>Escalas e configurações</li>
                <li>Banco de horas e ajustes</li>
                <li>Conversas de chat</li>
              </ul>
              <p className="text-destructive font-semibold">
                Esta ação NÃO pode ser desfeita!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirmar-exclusao-empresa"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sim, Excluir Empresa
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Edição de Empresa */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-editar-empresa">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Editar Empresa
            </DialogTitle>
            <DialogDescription>
              Atualize os dados cadastrais da empresa
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-razao-social">Razão Social *</Label>
                <Input
                  id="edit-razao-social"
                  data-testid="edit-razao-social"
                  value={editFormData.razao_social}
                  onChange={(e) => setEditFormData({...editFormData, razao_social: e.target.value})}
                  placeholder="Razão Social da Empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-nome-fantasia">Nome Fantasia</Label>
                <Input
                  id="edit-nome-fantasia"
                  data-testid="edit-nome-fantasia"
                  value={editFormData.nome_fantasia}
                  onChange={(e) => setEditFormData({...editFormData, nome_fantasia: e.target.value})}
                  placeholder="Nome Fantasia"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-cnpj">CNPJ</Label>
                <Input
                  id="edit-cnpj"
                  data-testid="edit-cnpj"
                  value={editFormData.cnpj}
                  onChange={(e) => setEditFormData({...editFormData, cnpj: e.target.value})}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-cnae">CNAE</Label>
                <Input
                  id="edit-cnae"
                  data-testid="edit-cnae"
                  value={editFormData.cnae}
                  onChange={(e) => setEditFormData({...editFormData, cnae: e.target.value})}
                  placeholder="0000-0/00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  data-testid="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  placeholder="empresa@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-telefone">Telefone</Label>
                <Input
                  id="edit-telefone"
                  data-testid="edit-telefone"
                  value={editFormData.telefone}
                  onChange={(e) => setEditFormData({...editFormData, telefone: e.target.value})}
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="edit-endereco">Endereço</Label>
                <Input
                  id="edit-endereco"
                  data-testid="edit-endereco"
                  value={editFormData.endereco}
                  onChange={(e) => setEditFormData({...editFormData, endereco: e.target.value})}
                  placeholder="Endereço completo"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleEditSubmit} disabled={saving} data-testid="btn-salvar-empresa">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpresasPage;
